/**
 * Canvas-based text-width measurement. Reuses a single offscreen context
 * to keep per-call cost near zero — useful when measuring many strings
 * for dropdown auto-sizing.
 *
 * Returns 0 in non-browser environments (server-render, tests without
 * jsdom canvas) so callers can fall back to a default width.
 */

let cachedContext: CanvasRenderingContext2D | null | undefined

function getContext(): CanvasRenderingContext2D | null {
  if (cachedContext !== undefined) return cachedContext
  if (typeof document === 'undefined') {
    cachedContext = null
    return null
  }
  if (typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('jsdom')) {
    cachedContext = null
    return null
  }
  const canvas = document.createElement('canvas')
  try {
    cachedContext = canvas.getContext('2d')
  } catch {
    cachedContext = null
  }
  return cachedContext
}

/**
 * Read the CSS `font` shorthand from a real DOM element. Use this on the
 * trigger button so measurements match exactly what the browser will
 * render — independent of any design-system class names.
 */
export function getElementFont(el: HTMLElement | null): string | null {
  if (!el) return null
  const style = window.getComputedStyle(el)
  // Canonical font shorthand: "<style> <weight> <size>/<line-height> <family>"
  // The browser-composed `font` is usually empty in CSS-modules; rebuild from parts.
  const weight = style.fontWeight || '400'
  const size = style.fontSize || '14px'
  const family = style.fontFamily || 'sans-serif'
  return `${weight} ${size} ${family}`
}

/** Measure a single string at the given CSS font. Returns pixel width. */
export function measureTextWidth(text: string, font: string): number {
  const ctx = getContext()
  if (!ctx) return 0
  ctx.font = font
  return ctx.measureText(text).width
}

/** Return the largest measured width across an array of strings. */
export function measureMaxTextWidth(texts: string[], font: string): number {
  if (texts.length === 0) return 0
  const ctx = getContext()
  if (!ctx) return 0
  ctx.font = font
  let max = 0
  for (const t of texts) {
    const w = ctx.measureText(t).width
    if (w > max) max = w
  }
  return max
}
